import { readIntercomWorkspaceId } from './intercom-contract';
import { describeMetaConnection } from './meta-contract';
import type { CatalogIntegration, Integration, IntegrationType } from './types';
import type { IntegrationAccount } from '@/types';

export const INTEGRATIONS: Record<IntegrationType, Integration> = {
	gorgias: {
		id: 'gorgias',
		name: 'Gorgias',
		description: 'Use Kim with Gorgias to manage your conversations in one place.',
		logo: '/assets/brand/gorgias.svg',
		tags: ['chat'],
		isConnected: false
	},
	helpdesk: {
		id: 'helpdesk',
		name: 'Kim Helpdesk',
		description: "Kim's built-in helpdesk.",
		logo: '/assets/brand/kim-ai-logo.svg',
		tags: ['chat'],
		isConnected: false
	},
	helpscout: {
		id: 'helpscout',
		name: 'Help Scout',
		description: 'Connect Help Scout to manage support conversations with Kim.',
		logo: '/assets/brand/helpscout.svg',
		tags: ['chat'],
		sheetSize: 'wide',
		isConnected: false
	},
	shopify: {
		id: 'shopify',
		name: 'Shopify',
		description: 'Shopify Connect helps your support team access customer details instantly.',
		logo: '/assets/brand/shopify.svg',
		tags: ['order-management'],
		isConnected: false
	},
	zoho: {
		id: 'zoho',
		name: 'Zoho',
		description: 'Connect Zoho Desk to sync support tickets with Kim.',
		logo: '/assets/brand/ZohoDesk.svg',
		tags: ['chat'],
		isConnected: false
	},
	smartrr: {
		id: 'smartrr',
		name: 'Smartrr',
		description: 'Connect Smartrr to access subscription and order data within Kim.',
		logo: '/assets/brand/smartrr.png',
		tags: ['subscription'],
		isConnected: false
	},
	skio: {
		id: 'skio',
		name: 'Skio',
		description:
			'Connect Skio to view and manage Shopify subscriptions within Kim.',
		logo: '/assets/brand/skio.png',
		tags: ['subscription'],
		isConnected: false
	},
	infoplus: {
		id: 'infoplus',
		name: 'InfoPlus',
		description: 'Connect InfoPlus to access warehouse inventory and order fulfillment data within Kim.',
		logo: '/assets/brand/infoplus.png',
		tags: ['ecommerce'],
		isConnected: false
	},
	clickpost: {
		id: 'clickpost',
		name: 'ClickPost',
		description: 'Connect ClickPost to access courier tracking and shipping data within Kim.',
		logo: '/assets/brand/clickpost.svg',
		tags: ['ecommerce'],
		isConnected: false
	},
	zendesk: {
		id: 'zendesk',
		name: 'Zendesk',
		description: 'Connect Zendesk to manage support tickets and customer conversations within Kim.',
		logo: '/assets/brand/zendesk.svg',
		tags: ['chat'],
		isConnected: false
	},
	google: {
		id: 'google',
		name: 'Google',
		description: 'Connect Google to use Docs and Sheets as knowledge sources for Kim.',
		logo: '/assets/brand/google.svg',
		tags: ['knowledge'],
		isConnected: false
	},
	judgeme: {
		id: 'judgeme',
		name: 'Judge.me',
		description: 'Connect Judge.me to access product reviews and ratings within Kim.',
		logo: '/assets/brand/judgeme.jpg',
		tags: ['reviews'],
		isConnected: false
	},
	richpanel: {
		id: 'richpanel',
		name: 'Richpanel',
		description: 'Connect Richpanel to manage support conversations and customer data within Kim.',
		logo: '/assets/brand/richpanel.png',
		tags: ['chat'],
		sheetSize: 'wide',
		isConnected: false
	},
	freshdesk: {
		id: 'freshdesk',
		name: 'Freshdesk',
		description: 'Connect Freshdesk to manage support tickets and customer conversations within Kim.',
		logo: '/assets/brand/freshdesk.svg',
		tags: ['chat'],
		isConnected: false
	},
	aftership: {
		id: 'aftership',
		name: 'AfterShip',
		description: 'Connect AfterShip to track shipments and returns across 1,000+ couriers within Kim.',
		logo: '/assets/brand/aftership.svg',
		tags: ['ecommerce'],
		isConnected: false
	},
	wonderment: {
		id: 'wonderment',
		name: 'Wonderment',
		description: 'Connect Wonderment to track shipments and surface post-purchase delivery status within Kim.',
		logo: '/assets/brand/wonderment.svg',
		tags: ['ecommerce'],
		isConnected: false
	},
	checkoutchamp: {
		id: 'checkoutchamp',
		name: 'Checkout Champ',
		description: 'Connect Checkout Champ to look up orders, charges and subscriptions, and resolve refunds, cancellations and address changes within Kim.',
		logo: '/assets/brand/checkoutchamp.png',
		tags: ['subscription'],
		isConnected: false
	},
	shipbob: {
		id: 'shipbob',
		name: 'ShipBob',
		description: 'Connect ShipBob to resolve order tracking and return status for 3PL-fulfilled customers.',
		logo: '/assets/brand/shipbob.svg',
		tags: ['ecommerce'],
		isConnected: false
	},
	recharge: {
		id: 'recharge',
		name: 'Recharge',
		description: 'Connect Recharge to access subscription and order data within Kim.',
		logo: '/assets/brand/recharge.jpeg',
		tags: ['subscription'],
		isConnected: false
	},
	shipstation: {
		id: 'shipstation',
		name: 'ShipStation',
		description: 'Connect ShipStation to access order and shipment data within Kim.',
		logo: '/assets/brand/shipstation.svg',
		tags: ['ecommerce'],
		isConnected: false
	},
	yotpo: {
		id: 'yotpo',
		name: 'Yotpo',
		description: 'Connect Yotpo to access loyalty, reviews, and referrals data within Kim.',
		logo: '/assets/brand/yotpo.svg',
		tags: ['reviews'],
		isConnected: false
	},
	slack: {
		id: 'slack',
		name: 'Slack',
		description: 'Connect Slack so Kim can read channels, post messages, and reply to threads in your workspace.',
		logo: '/assets/brand/slack.svg',
		tags: ['messaging'],
		isConnected: false
	},
	loopreturns: {
		id: 'loopreturns',
		name: 'Loop Returns',
		description: 'Connect Loop Returns to access returns and exchanges data within Kim.',
		logo: '/assets/brand/loop.svg',
		tags: ['ecommerce'],
		isConnected: false
	},
	klaviyo: {
		id: 'klaviyo',
		name: 'Klaviyo',
		description: 'Connect Klaviyo to access email and SMS marketing profiles, campaigns, and suppression data within Kim.',
		logo: '/assets/brand/klaviyo.svg',
		tags: ['messaging'],
		isConnected: false
	},
	usps: {
		id: 'usps',
		name: 'USPS',
		description: 'Connect USPS to track shipments and get real-time delivery status within Kim.',
		logo: '/assets/brand/usps.svg',
		tags: ['ecommerce'],
		isConnected: false
	},
	reviewsio: {
		id: 'reviewsio',
		name: 'Reviews.io',
		description: 'Connect Reviews.io to access product reviews, reply to customers, and manage review invitations within Kim.',
		logo: '/assets/brand/reviewsio.svg',
		tags: ['reviews'],
		isConnected: false
	},
	trustpilot: {
		id: 'trustpilot',
		name: 'Trustpilot',
		description: 'Connect Trustpilot to access product reviews and reply to customers within Kim.',
		logo: '/assets/brand/trustpilot.svg',
		tags: ['reviews'],
		isConnected: false
	},
	kustomer: {
		id: 'kustomer',
		name: 'Kustomer',
		description: 'Connect Kustomer to manage support conversations and reply to customers within Kim.',
		logo: '/assets/brand/kustomer.svg',
		tags: ['chat'],
		isConnected: false
	},
	unicommerce: {
		id: 'unicommerce',
		name: 'Unicommerce',
		description: 'Connect Unicommerce to access order management and fulfillment data within Kim.',
		logo: '/assets/brand/unicommerce.svg',
		tags: ['order-management'],
		isConnected: false
	},
	proship: {
		id: 'proship',
		name: 'ProShip',
		description: 'Connect ProShip to access shipping and delivery partner data within Kim.',
		logo: '/assets/brand/proship.svg',
		tags: ['ecommerce'],
		isConnected: false
	},
	royalmail: {
		id: 'royalmail',
		name: 'Royal Mail',
		description: 'Connect Royal Mail to track UK parcels and fetch proof of delivery within Kim.',
		logo: '/assets/brand/royalmail.svg',
		tags: ['ecommerce'],
		isConnected: false
	},
	warehance: {
		id: 'warehance',
		name: 'Warehance',
		description: 'Connect Warehance to read live shipment and fulfillment status from the warehouse within Kim.',
		logo: '/assets/brand/warehance.svg',
		tags: ['order-management'],
		isConnected: false
	},
	mintsoft: {
		id: 'mintsoft',
		name: 'Mintsoft',
		description: 'Connect Mintsoft to read warehouse order status, despatch and tracking for where-is-my-order questions within Kim.',
		logo: '/assets/brand/mintsoft.svg',
		tags: ['order-management'],
		isConnected: false
	},
	gladly: {
		id: 'gladly',
		name: 'Gladly',
		description: 'Connect Gladly to manage support conversations and customer data within Kim.',
		logo: '/assets/brand/gladly.png',
		tags: ['chat'],
		isConnected: false
	},
	intercom: {
		id: 'intercom',
		name: 'Intercom',
		description: 'Connect Intercom to manage support conversations and customer data within Kim.',
		logo: '/assets/brand/intercom.svg',
		tags: ['chat'],
		isConnected: false
	},
	meta: {
		id: 'meta',
		name: 'Meta',
		description: 'Connect Facebook Pages and Instagram accounts so Kim can moderate comments.',
		logo: '/assets/brand/meta.svg',
		tags: ['social'],
		// One Meta connection holds several Pages and Instagram accounts, each with its own
		// capability chips — the narrow sheet cannot hold that list legibly.
		sheetSize: 'wide',
		isConnected: false
	},
	smile: {
		id: 'smile',
		name: 'Smile.io',
		description:
			'Connect Smile.io to look up loyalty points, VIP tiers, and reward codes within Kim.',
		logo: '/assets/brand/smile.svg',
		tags: ['reviews'],
		isConnected: false
	}
};

/**
 * Get connected name from authMetaData based on integration type
 */
const getConnectedName = (integrationName: string, authMetaData: Record<string, unknown>): string | undefined => {
	switch (integrationName.toLowerCase()) {
		case 'gorgias':
			return typeof authMetaData.account === 'string' ? authMetaData.account : undefined;
		case 'helpscout': {
			const companyName = authMetaData.companyName ?? authMetaData.company_name;
			return typeof companyName === 'string' ? companyName : undefined;
		}
		case 'shopify':
			return typeof authMetaData.shop === 'string' ? authMetaData.shop : undefined;
		case 'klaviyo':
			return undefined;
		case 'auspost':
			return typeof authMetaData.account_number === 'string' ? authMetaData.account_number : undefined;
		case 'zoho': {
			const email = authMetaData.from_email ?? authMetaData.fromEmail ?? authMetaData.email;
			return typeof email === 'string' ? email : undefined;
		}
		case 'smartrr':
		case 'skio':
		case 'smile':
			return undefined;
		case 'infoplus': {
			const baseUrl = authMetaData.baseUrl ?? authMetaData.base_url;
			if (typeof baseUrl === 'string') {
				try {
					return new URL(baseUrl).origin.replace(/^https?:\/\//, '');
				} catch {
					return baseUrl;
				}
			}
			return undefined;
		}
		case 'clickpost':
			return typeof authMetaData.username === 'string' ? authMetaData.username : undefined;
		case 'zendesk':
			// OAuth connections have no admin_email — the subdomain identifies the
			// account. Falls back to admin_email for pre-OAuth connections.
			if (typeof authMetaData.subdomain === 'string') return authMetaData.subdomain;
			return typeof authMetaData.admin_email === 'string' ? authMetaData.admin_email : undefined;
		case 'google':
			return typeof authMetaData.email === 'string' ? authMetaData.email : undefined;
		case 'judgeme':
			return typeof authMetaData.domain === 'string' ? authMetaData.domain : undefined;
		case 'richpanel':
			return typeof authMetaData.domain === 'string' ? authMetaData.domain : undefined;
		case 'freshdesk':
			return typeof authMetaData.subdomain === 'string' ? authMetaData.subdomain : undefined;
		case 'aftership':
		case 'wonderment':
		case 'shipbob':
		case 'recharge':
			return undefined;
		// Royal Mail, like USPS, issues a client id per portal application — the closest thing to an
		// account identifier either connection carries.
		// Checkout Champ authenticates as a dedicated API user; its username identifies the connection.
		case 'checkoutchamp':
			return typeof authMetaData.login_id === 'string' ? authMetaData.login_id : undefined;
		case 'usps':
		case 'royalmail':
			return typeof authMetaData.client_id === 'string' ? authMetaData.client_id : undefined;
		case 'shipstation':
		case 'yotpo':
		case 'slack':
		case 'loopreturns':
			return undefined;
		case 'reviewsio':
			return typeof authMetaData.store_id === 'string' ? authMetaData.store_id : undefined;
		case 'trustpilot':
			return typeof authMetaData.business_unit_id === 'string' ? authMetaData.business_unit_id : undefined;
		// Only organization-level Warehance keys carry a client_id; client-level keys show nothing.
		case 'warehance':
			return typeof authMetaData.client_id === 'string' ? authMetaData.client_id : undefined;
		// The client a 3PL key is scoped to, else the Mintsoft user Kim signs in as.
		case 'mintsoft': {
			const name = authMetaData.client_name ?? authMetaData.client_id ?? authMetaData.username;
			return typeof name === 'string' && name ? name : undefined;
		}
		case 'kustomer':
			return typeof authMetaData.org_name === 'string' ? authMetaData.org_name : undefined;
		case 'gladly':
			return typeof authMetaData.organization === 'string' ? authMetaData.organization : undefined;
		case 'intercom':
			return readIntercomWorkspaceId(authMetaData);
		// One Meta connection covers several accounts, so there is no single name to show.
		case 'meta':
			return describeMetaConnection(authMetaData);
		default:
			return undefined;
	}
};

/**
 * Primary row first, then oldest (lowest id) first — the same order machine and the tool
 * server resolve "which store" with, so the UI shows the store the account actually uses.
 */
/**
 * Split a provider's rows by the brand they belong to, keeping the order they arrived in
 * so the section order stays the backend's. Rows with no brand group together under one
 * key — "belongs to no brand" is one bucket, not one bucket per row.
 */
const groupByBusinessUnit = (rows: IntegrationAccount[]): IntegrationAccount[][] => {
	const byUnit = new Map<string, IntegrationAccount[]>();
	rows.forEach((row) => {
		const key = row.buId ?? 'none';
		byUnit.set(key, [...(byUnit.get(key) ?? []), row]);
	});
	return [...byUnit.values()];
};

const sortPrimaryFirst = (rows: IntegrationAccount[]): IntegrationAccount[] =>
	[...rows].sort((a, b) => Number(Boolean(b.isPrimary)) - Number(Boolean(a.isPrimary)) || Number(a.id) - Number(b.id));

/**
 * Merge API integration data with static integration config
 */
export const mergeIntegrationData = (
	staticIntegrations: Record<IntegrationType, Integration>,
	apiIntegrations: IntegrationAccount[]
): CatalogIntegration[] => {
	const merged: CatalogIntegration[] = [];

	// Group API rows by name. Most providers have one row; Shopify has one per store.
	const apiMap = new Map<string, IntegrationAccount[]>();
	apiIntegrations.forEach((apiIntegration) => {
		if (apiIntegration.name) {
			const key = apiIntegration.name.toLowerCase();
			apiMap.set(key, [...(apiMap.get(key) ?? []), apiIntegration]);
		}
	});

	// Merge static integrations with API data
	Object.values(staticIntegrations).forEach((staticIntegration) => {
		const catalogId = staticIntegration.id as IntegrationType;
		const rows = apiMap.get(staticIntegration.id) ?? [];

		if (rows.length === 0) {
			merged.push({ ...staticIntegration, catalogId });
			return;
		}

		// One card per provider PER BRAND, which is the only split that keeps both
		// halves right: grouping on the provider alone folded one brand's Shopify
		// store into another brand's card and counted it as "+1", while one card per
		// connection lost the "+N" that several stores inside ONE brand still need.
		//
		// With a brand selected the API has already filtered to it, so there is one
		// group and this is exactly the pre-brand behaviour.
		groupByBusinessUnit(rows).forEach((rowsForUnit) => {
			const instances = sortPrimaryFirst(rowsForUnit);
			const apiIntegration = instances[0]!;
			const connectedName = apiIntegration.authMetaData
				? getConnectedName(apiIntegration.name, apiIntegration.authMetaData)
				: undefined;
			merged.push({
				...staticIntegration,
				catalogId,
				id: apiIntegration.id?.toString() ?? staticIntegration.id,
				isConnected: instances.some((i) => i.isConnected),
				// "acme-us +1": the primary store, and how many more this brand has.
				connectedName:
					connectedName && instances.length > 1 ? `${connectedName} +${instances.length - 1}` : connectedName,
				authMetaData: apiIntegration.authMetaData,
				buId: apiIntegration.buId ?? null,
				instances
			});
		});
	});

	return merged;
};

/**
 * An integration's display name by its provider id ("loopreturns" → "Loop Returns"), or null if
 * unknown. Only the catalog's own entries count: a name every object has, like "constructor", is
 * not a provider.
 */
export function getIntegrationName(provider: string): string | null {
	const id = provider.trim().toLowerCase();
	return Object.hasOwn(INTEGRATIONS, id) ? INTEGRATIONS[id as IntegrationType].name : null;
}

/**
 * Returns the brand logo path for a given integration/provider name, or null if unknown.
 * Handles common aliases (e.g. "loop" → "loopreturns", "zohoDesk" → "zoho") and a product
 * of a brand by its brand ("yotpo_loyalty" → "yotpo").
 */
export function getBrandLogo(name: string): string | null {
	const key = name.toLowerCase().replace(/\s+/g, '');
	const ALIASES: Record<string, IntegrationType> = {
		loop: 'loopreturns',
		zohodesk: 'zoho',
		'judge.me': 'judgeme',
		reviews: 'reviewsio',
		'reviews.io': 'reviewsio',
	};
	const lookup = (candidate: string): string | null =>
		INTEGRATIONS[(ALIASES[candidate] ?? candidate) as IntegrationType]?.logo ?? null;
	const brand = key.split(/[_-]/)[0] ?? key;
	return lookup(key) ?? (brand !== key ? lookup(brand) : null);
}
